import { CircularProgress, Stack, TextField, Typography } from '@mui/material';
import { FC } from 'react';

import AutocompleteLockersInput from '@components/Inputs/AutocompleteLockers/AutocompleteLockers';
import isString from 'lodash/isString';
import { toast } from 'sonner';
import { useBarcodeScanner } from 'src/hooks/useBarcodeReader';
import { useGetAllLockersBanksMapQuery } from 'src/redux/api/lockers';

import { sxSection } from '../styles';
import { LocationLockersError, LocationState, LocationStateChange } from '../useLocationId';
import CellsSizeSelect from './CellsSizeSelect';

type Props = {
  error: LocationLockersError;
  spot: LocationState;
  onChange: (values: LocationStateChange) => void;
};

const lockerIdKeys = ['manufactureID', 'b'];
const cellIdKeys = ['manufacturerLockerID', 'c'];

const parse = (str: string) => {
  const a = str.split(',');
  return a.map((param) => {
    // eslint-disable-next-line
    let [z, x] = param.split(":");
    if (z[0] !== '"') {
      z = `"${z}"`;
    }
    return [z, x].join(":");
  }).join(',');
}

const LockerContent: FC<Props> = ({ spot, onChange, error }) => {
  const { data, isLoading } = useGetAllLockersBanksMapQuery();

  useBarcodeScanner(
    (scan) => {
      if (isLoading) return;

      try {
        const parsedJson = scan[0] === '{' ? JSON.parse(scan) : JSON.parse(`{${parse(scan)}}`);
        const lockerId = parsedJson[lockerIdKeys.find((key) => parsedJson[key]) || ''];
        const lockerCell = parsedJson[cellIdKeys.find((key) => parsedJson[key]) || ''];

        if (!isString(lockerId) || !isString(lockerCell) || !data) {
          throw new Error('error');
        }

        const item = Object.values(data).find((locker) => locker.externalId === lockerId);

        if (!item) {
          throw new Error('error');
        }

        onChange({
          lockerBankId: item.id,
          lockerCell,
        });
      } catch (err) {
        toast.error('Unable to identify locker information');
      }
    },
    undefined,
    { withPrint: false, maxInterval: 300, listenerId: 'barcode-reader-listener-id' },
  );

  const selectedLockerBank = spot?.lockerBankId ? data?.[spot?.lockerBankId] : null;

  if (isLoading) {
    return <CircularProgress sx={{ alignSelf: 'center' }} />;
  }

  return (
    <>
      <Stack sx={sxSection} spacing={3}>
        <Stack direction="row" spacing={0.8}>
          <Typography variant="subtitle1">Storage Group</Typography>
        </Stack>
        <AutocompleteLockersInput
          multiple={false}
          key={spot.lockerBankId}
          onChange={(ids) => {
            const id = ids[0];
            const nextBank = data?.[id];
            if (!nextBank) {
              onChange({});
              return;
            }
            const additional = nextBank.isRearLoaded ? { lockerCell: spot.lockerCell } : { }
            onChange({
              lockerBankId: id,
              ...additional,
            });
          }}
          error={error.lockerBankId}
          selectDefaultIfOneOption
          defaultValue={spot.lockerBankId}
        />
      </Stack>
      {selectedLockerBank && (
        <Stack sx={sxSection} spacing={3}>
          {selectedLockerBank.isRearLoaded ? (
          <>
            <Stack direction="row" spacing={0.8}>
              <Typography variant="subtitle1">Item</Typography>
            </Stack>
            <TextField
              value={spot.lockerCell || ''}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                onChange({
                  lockerBankId: spot.lockerBankId,
                  lockerCell: event.target.value,
                });
              }}
              error={!!error.lockerCell}
              helperText={error.lockerCell}
              fullWidth
              label="Locker Cell"
              required
            />
            </>
          ) : (
            <CellsSizeSelect
              lockerBank={selectedLockerBank}
              selectedCellSize={spot.lockerCellSize}
              onChange={(cellSize) =>
                onChange({
                  lockerBankId: spot.lockerBankId,
                  lockerCellSize: cellSize,
                })
              }
            />
          )}
        </Stack>
      )}
    </>
  );
};

export default LockerContent;
